import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import VendorVideoPanel from "@/components/VendorVideoPanel";
import MarketingVideoPanel from "@/components/MarketingVideoPanel";
import VideoShareSmsPanel from "@/components/VideoShareSmsPanel";
import {
  Film, Loader2, RefreshCw, Download, Globe, GlobeLock, Clapperboard, Sparkles, AlertCircle,
  Smartphone, CalendarClock, Clock, Activity,
} from "lucide-react";
import ShareMenu from "@/components/ShareMenu";

const PHASE_LABEL = {
  queued: "Queued",
  init: "Initializing",
  tts: "Generating narration",
  capture: "Recording portal walkthrough",
  audio_concat: "Mixing audio",
  title_cards: "Rendering branded title cards",
  normalize: "Normalizing video",
  mux: "Encoding final MP4",
  done: "Ready",
};

const POLL_MS = 4000;

// Reliable cross-platform video download.
//
// Strategy:
//   1. Fetch the file as a blob (single network round-trip).
//   2. Synthesise a click on a blob:URL anchor with `download` attribute —
//      this works in every desktop browser (Chrome/Edge/Firefox/Safari) and
//      modern Android/iOS Safari (16+) regardless of server Content-Disposition.
//   3. If anything fails (e.g. older iOS dropping the blob anchor), fall back
//      to opening the URL in a new tab so the user can long-press → Save.
//
// We deliberately do NOT use the Web Share API here — admins want a download,
// not a share sheet, and `navigator.share({files})` hijacks the flow on
// browsers where canShare({files}) is true.
async function downloadBlob(url, filename) {
  try {
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    return true;
  } catch (e) {
    // Last-resort fallback for browsers that refuse the blob trick — opens the
    // raw URL so the user can right-click / long-press → Save.
    try {
      window.open(url, "_blank", "noopener,noreferrer");
      toast.message(
        "Opened video in a new tab — long-press the player to save it.",
      );
      return true;
    } catch {
      toast.error(`Download failed: ${e.message || e}`);
      return false;
    }
  }
}

// Pick the highest-converting channel with at least 2 hits + 1 conversion;
// tie-break by absolute conversions then hits. Returns null if none qualify.
function pickTopConvertingChannel(byChannel) {
  if (!Array.isArray(byChannel) || !byChannel.length) return null;
  const eligible = byChannel.filter((c) => c.conversions > 0 && c.hits >= 2);
  if (!eligible.length) return null;
  eligible.sort(
    (a, b) =>
      (b.conversion_rate_pct || 0) - (a.conversion_rate_pct || 0) ||
      (b.conversions || 0) - (a.conversions || 0) ||
      (b.hits || 0) - (a.hits || 0),
  );
  return eligible[0];
}

export default function AdminDemoVideo() {
  const [state, setState] = useState({ flagship_3min: {}, full_7min: {} });
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [topChannel, setTopChannel] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/demo-video/status");
      setState(r.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Surface the historically-best converting channel above each Share button so
  // every share is a closed loop, not a guess. Refresh on a slow cadence so it
  // stays current without spamming the endpoint.
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const r = await api.get("/admin/share-events/summary");
        if (!alive) return;
        setTopChannel(pickTopConvertingChannel(r.data?.by_channel));
      } catch {
        /* attribution is best-effort */
      }
    };
    pull();
    const t = setInterval(pull, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const trigger = async (cut) => {
    setBusy(true);
    try {
      await api.post(`/admin/demo-video/render?cut=${cut}`);
      toast.success(
        cut === "both"
          ? "Render queued for both cuts. This takes ~10-15 min."
          : `${cut} render queued. ETA: ${cut === "flagship_3min" ? "4-6" : "8-12"} min.`
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const togglePublish = async (cut) => {
    setPublishing(true);
    try {
      const next = {
        flagship_3min: state.flagship_3min?.is_published || false,
        full_7min: state.full_7min?.is_published || false,
      };
      next[cut] = !next[cut];
      await api.post("/admin/demo-video/publish", next);
      toast.success(next[cut] ? `Published ${cut} to /demo` : `Unpublished ${cut}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Demo Video"
      title="Auto-rendered walkthrough video"
      intro="Generate a high-quality MP4 walkthrough of the demo portal — clean voice narration over a real Playwright recording. Two cuts: flagship 3-min for sales emails + full 7-min for the marketing page."
      actions={
        <button
          onClick={load}
          data-testid="demo-video-refresh"
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh status
        </button>
      }
    >
      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 mb-5 text-xs text-blue-900 leading-relaxed flex gap-2">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <b>How this works.</b> Server-side, we mint a fresh demo magic-link, drive
          the demo portal end-to-end with Playwright while OpenAI TTS generates the narration
          (voice <code className="bg-white/60 px-1 rounded">sage</code>, model <code className="bg-white/60 px-1 rounded">tts-1-hd</code>),
          then mux it all into a 1920×1080 MP4. <b>Render time:</b> 4-6 min for flagship, 8-12 min for full.
        </div>
      </div>

      {state.schedule && (
        <div
          className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 mb-5 text-xs text-violet-900 leading-relaxed flex items-center gap-2"
          data-testid="demo-video-schedule-banner"
        >
          <CalendarClock className="w-4 h-4 shrink-0" />
          <div>
            <b>Auto-render schedule:</b> {state.schedule.cadence}.{" "}
            {state.schedule.enabled ? (
              <>
                Next run:{" "}
                <b className="tabular-nums" data-testid="demo-video-next-run">
                  {new Date(state.schedule.next_run_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </b>
                . Both cuts re-render automatically — no clicks required.
              </>
            ) : (
              <span>Disabled (DEMO_VIDEO_AUTO_RENDER=0). Trigger manually from below.</span>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <CutCard
          title="Flagship · 3 minute cut"
          tagline="High-conversion sales asset · share in emails, demos, and ads"
          cut="flagship_3min"
          state={state.flagship_3min}
          onRender={() => trigger("flagship_3min")}
          onTogglePublish={() => togglePublish("flagship_3min")}
          busy={busy}
          publishing={publishing}
          topChannel={topChannel}
        />
        <CutCard
          title="Full · 7 minute cut"
          tagline="Comprehensive walkthrough · for landing-page hero + onboarding"
          cut="full_7min"
          state={state.full_7min}
          onRender={() => trigger("full_7min")}
          onTogglePublish={() => togglePublish("full_7min")}
          busy={busy}
          publishing={publishing}
          topChannel={topChannel}
        />
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button
          onClick={() => trigger("both")}
          disabled={busy}
          data-testid="demo-video-render-both"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          <Clapperboard className="w-4 h-4" />
          {busy ? "Queueing…" : "Render both cuts"}
        </button>
        <span className="text-xs text-slate-500">≈ 12-18 minutes total — runs as background tasks.</span>
      </div>

      <ShareAttributionPanel />
      <VideoShareSmsPanel />
      <MarketingVideoPanel />
      <VendorVideoPanel />
    </ToolPageShell>
  );
}

function ShareAttributionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/share-events/summary")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  if (!data.total_hits) {
    return (
      <div
        className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-xs text-slate-500"
        data-testid="share-attribution-empty"
      >
        <span className="font-semibold text-slate-700">Share attribution.</span>{" "}
        No share-tagged hits yet — links shared from the Share menu carry{" "}
        <code className="bg-slate-100 px-1 rounded">?utm_source=…&amp;utm_medium=share</code>{" "}
        and traffic will start appearing here on first click.
      </div>
    );
  }

  const tone = (k) =>
    ({
      linkedin: "bg-blue-600",
      facebook: "bg-blue-700",
      twitter: "bg-slate-900",
      whatsapp: "bg-emerald-500",
      slack: "bg-violet-600",
      email: "bg-slate-500",
      sms: "bg-slate-500",
      direct: "bg-slate-700",
    }[k] || "bg-slate-400");

  const max = Math.max(...data.by_channel.map((c) => c.hits), 1);
  return (
    <div
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="share-attribution-panel"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
            Share attribution
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {data.total_hits} demo-page visit{data.total_hits === 1 ? "" : "s"} · {data.total_conversions} demo request{data.total_conversions === 1 ? "" : "s"}
            {data.total_hits ? ` · ${data.overall_conversion_rate_pct}% overall` : ""}.
            UTM-tagged on every link the Share menu hands out.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          last 14 days
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-5">
        <ul className="space-y-1.5" data-testid="share-attribution-channels">
          {data.by_channel.slice(0, 8).map((c) => (
            <li
              key={c.channel}
              className="flex items-center gap-3 text-sm"
              data-testid={`share-channel-${c.channel}`}
            >
              <span className="w-20 text-xs font-semibold text-slate-700 capitalize shrink-0">
                {c.channel}
              </span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${tone(c.channel)}`}
                  style={{ width: `${(c.hits / max) * 100}%` }}
                />
              </div>
              <span
                className="w-12 text-right tabular-nums text-xs font-bold text-slate-900 shrink-0"
                title="Hits"
              >
                {c.hits}
              </span>
              <span
                className={`w-20 text-right tabular-nums text-[11px] font-semibold shrink-0 ${
                  c.conversions > 0 ? "text-emerald-700" : "text-slate-400"
                }`}
                title="Conversions"
                data-testid={`share-channel-${c.channel}-conv`}
              >
                {c.conversions} conv
                {c.hits > 0 && c.conversions > 0 && (
                  <span className="text-slate-400 font-normal ml-1">
                    · {c.conversion_rate_pct}%
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        <Sparkline points={data.timeseries} />
      </div>
    </div>
  );
}

function Sparkline({ points }) {
  const max = Math.max(...points.map((p) => p.hits), 1);
  return (
    <div data-testid="share-attribution-sparkline">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
        Daily hits
      </div>
      <div className="flex items-end gap-0.5 h-16">
        {points.map((p) => (
          <div
            key={p.date}
            title={`${p.date} · ${p.hits} hit${p.hits === 1 ? "" : "s"}`}
            className="flex-1 bg-slate-300 hover:bg-slate-500 transition-colors rounded-sm"
            style={{ height: `${(p.hits / max) * 100}%`, minHeight: 2 }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-slate-400 mt-1 tabular-nums">
        <span>{points[0]?.date.slice(5)}</span>
        <span>{points[points.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function CutCard({ title, tagline, cut, state, onRender, onTogglePublish, busy, publishing, topChannel }) {
  const status = state?.status || "idle";
  const phase = state?.phase;
  const url = state?.url ? resolveAssetUrl(state.url) : null;
  const mobileUrl = state?.mobile_url ? resolveAssetUrl(state.mobile_url) : null;
  const mobileBytes = state?.mobile?.size_bytes;

  const renderedAgo = state?.rendered_at ? _ago(state.rendered_at) : null;
  const [downloading, setDownloading] = useState(null); // "main" | "mobile" | null

  const handleDownload = async (variant) => {
    const target = variant === "mobile" ? mobileUrl : url;
    const filename =
      variant === "mobile" ? `nodera-${cut}-mobile.mp4` : `nodera-${cut}.mp4`;
    if (!target) return;
    setDownloading(variant);
    const ok = await downloadBlob(target, filename);
    if (ok) toast.success(`${filename} download started.`);
    setDownloading(null);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`cut-card-${cut}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">{title}</div>
          <p className="text-xs text-slate-500 mt-1">{tagline}</p>
        </div>
        <Film className="w-5 h-5 text-slate-400 mt-1" />
      </div>

      {/* Last rendered + A/V sync health pills */}
      {state?.rendered_at && (
        <div className="mt-3 flex items-center flex-wrap gap-1.5">
          <div
            className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 rounded-full px-3 py-1 text-[11px] font-semibold"
            data-testid={`cut-rendered-pill-${cut}`}
            title={new Date(state.rendered_at).toLocaleString()}
          >
            <Clock className="w-3 h-3" />
            Last rendered {renderedAgo}
            {state.duration_sec && (
              <span className="text-slate-400">· {_fmtDuration(state.duration_sec)}</span>
            )}
            {state.size_bytes && (
              <span className="text-slate-400">· {(state.size_bytes / 1e6).toFixed(1)} MB</span>
            )}
          </div>
          {state?.av_sync && (
            <div
              data-testid={`cut-avsync-pill-${cut}`}
              title={`Video ${state.av_sync.video_duration_sec}s · Audio ${state.av_sync.audio_duration_sec}s. Anything < 0.3s is imperceptible.`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
                state.av_sync.tone === "emerald"
                  ? "bg-emerald-100 text-emerald-800"
                  : state.av_sync.tone === "amber"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-rose-100 text-rose-800"
              }`}
            >
              <Activity className="w-3 h-3" />
              A/V sync · Δ{state.av_sync.delta_sec}s
            </div>
          )}
        </div>
      )}

      {/* Video preview */}
      <div className="mt-4 rounded-xl bg-slate-900 aspect-video overflow-hidden flex items-center justify-center" data-testid={`cut-preview-${cut}`}>
        {url ? (
          <video src={url} controls className="w-full h-full" />
        ) : status === "running" ? (
          <div className="text-center text-slate-300">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-400" />
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-300">{PHASE_LABEL[phase] || "Working"}</div>
            <div className="text-[10px] text-slate-400 mt-1">This page auto-refreshes every 4s</div>
          </div>
        ) : status === "failed" ? (
          <div className="text-center text-rose-300">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            <div className="text-xs">Render failed</div>
            <div className="text-[10px] text-rose-400/80 mt-1 max-w-xs px-3 truncate" title={state.error}>{state.error}</div>
          </div>
        ) : (
          <div className="text-center text-slate-400">
            <Film className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="text-xs">No render yet — click Generate to start.</div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={onRender}
          disabled={busy || status === "running"}
          data-testid={`cut-render-${cut}`}
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          <Clapperboard className="w-3.5 h-3.5" />
          {status === "running" ? "Rendering…" : (url ? "Regenerate" : "Generate")}
        </button>
        {url && (
          <button
            type="button"
            onClick={() => handleDownload("main")}
            disabled={downloading === "main"}
            data-testid={`cut-download-${cut}`}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
          >
            {downloading === "main" ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Downloading…
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" /> Download MP4
              </>
            )}
          </button>
        )}
        {mobileUrl && (
          <button
            type="button"
            onClick={() => handleDownload("mobile")}
            disabled={downloading === "mobile"}
            data-testid={`cut-download-mobile-${cut}`}
            title="720p / ~1Mbps · CDN-friendly variant for marketing-site embeds"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
          >
            {downloading === "mobile" ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> 720p…
              </>
            ) : (
              <>
                <Smartphone className="w-3.5 h-3.5" /> 720p
                {mobileBytes && (
                  <span className="text-slate-400 font-normal">
                    · {(mobileBytes / 1e6).toFixed(1)} MB
                  </span>
                )}
              </>
            )}
          </button>
        )}
        {url && (
          <button
            onClick={onTogglePublish}
            disabled={publishing}
            data-testid={`cut-publish-${cut}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60 ${
              state.is_published
                ? "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {state.is_published ? (<><GlobeLock className="w-3.5 h-3.5" /> Unpublish from /demo</>) : (<><Globe className="w-3.5 h-3.5" /> Publish to /demo</>)}
          </button>
        )}
        {url && state.is_published && (
          <ShareMenu
            url={`${window.location.origin}/demo`}
            title={cut === "flagship_3min"
              ? "Nodera Health · 3-minute walkthrough"
              : "Nodera Health · full 7-minute walkthrough"}
            message={cut === "flagship_3min"
              ? "A 3-minute tour of the Nodera Health operating layer for cash-pay clinics."
              : "Watch a full 7-minute walkthrough of how Nodera Health builds the operating layer for cash-pay clinics — the Cash-Pay Opportunity Scanner, vendor matching, intelligence center, and more."}
            testIdPrefix={`cut-share-${cut}`}
            topChannel={topChannel}
          />
        )}
      </div>
    </div>
  );
}

function _ago(iso) {
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function _fmtDuration(s) {
  const sec = Math.round(s);
  const m = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${m}m ${ss}s`;
}
